// AnalyticsDialog.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';
import { useDispatch, useSelector } from 'react-redux';
import { toggleDialog } from '../../../../redux/Slices/Global/globalSlice';
import { useGetStatistics, useSendActionDevice } from '../../../../queries';
import AnalyticsDetailDialog from './AnalyticsDetailDialog';

function AnalyticsDialog() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openDialog } = useSelector((state) => state.global);
    const { selectedDevice } = useSelector((state) => state.device);
    const [isWaitingForAnalytics, setIsWaitingForAnalytics] = useState(false);
    const deviceAction = useSendActionDevice()

    const visible = openDialog.analyticsDialog;
    const deviceId = selectedDevice?.id;
    const [dataItem, setDataItem] = useState()
    const {
        data,
        isLoading,
        isFetching,
        refetch,
    } = useGetStatistics(deviceId, visible);

    // Short burst polling: every 2s for up to 20s or until we get non-empty data
    const burstStopAtRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        // Start burst on open
        if (visible && deviceId) {
            burstStopAtRef.current = Date.now() + 20_000; // 20s window

            // Clear any old interval just in case
            if (intervalRef.current) clearInterval(intervalRef.current);

            intervalRef.current = setInterval(async () => {
                const now = Date.now();
                const gotData = Array.isArray(data) ? data.length > 0 : !!data; // adjust to your shape

                if (gotData || now >= burstStopAtRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    return;
                }

                await refetch(); // pull again
            }, 2_000); // every 2s
        }

        // Cleanup on close/unmount
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
        };
    }, [visible, deviceId, refetch, data]);

    const onClose = () => dispatch(toggleDialog("analyticsDialog"));

    const AIAnalytics = useCallback(async (deviceID, action) => {
        setIsWaitingForAnalytics(true);
        try {
            await deviceAction.mutateAsync(
                { id: deviceID, data: { action: action.toLowerCase() } },
                {
                    onSuccess: async () => {
                        // Open dialog immediately (it will show loading/empty state)
                        setIsWaitingForAnalytics(false);
                        refetch()
                    },
                }
            );
        } catch (err) {
            setIsWaitingForAnalytics(false);

            // your error handler
            console.error(err);
        }
    }, [deviceAction]);



    const footer = (
        <>
            <Button label={t('buttons.close')} icon="pi pi-times" className="p-button-text" onClick={onClose} />
            {/* Add actions if you need later */}
        </>
    );

    // UI states
    const isEmpty = !isLoading && !isFetching && (!data || (Array.isArray(data) && data.medias.length === 0));


    return (
        <Dialog
            visible={visible}
            style={{ width: '40vw', minHeight: '50vh' }}
            breakpoints={{ '960px': '95vw' }}
            header={t('devices.setup_ai_analytics')}
            modal
            footer={footer}
            onHide={onClose}
        >
            <div className="flex justify-between px-4">
                <div className="">
                    {isLoading && (
                        <div className="flex justify-center  py-4    items-center">
                            <i className="fa-solid fa-spinner fa-spin me-2" />
                            <span>{t('devices.setup_loading_analytics')}</span>
                        </div>
                    )}

                    {/* {!isLoading && isFetching && (
                        <div className="flex  justify-center  items-center py-2 small text-muted">
                            Refreshing…
                        </div>
                    )} */}
                </div>
                <Button label={t('buttons.refresh')} icon="fa-solid fa-arrows-rotate"
                    loading={deviceAction.isLoading || isFetching}
                    disabled={isFetching || isWaitingForAnalytics}
                    className="p-button-text"
                    onClick={() => AIAnalytics(deviceId, "get_viewers")} />


            </div>



            {data?.medias?.length < 1 && (
                <div className="py-4 flex flex-col h-100 w-100 justify-center  items-center h-100 text-center text-muted">
                    <div className="mb-2">
                        <i className="fa-solid fa-chart-bar" />
                    </div>
                    <div>{t('devices.setup_no_analytics_yet')}</div>
                </div>
            )}

            {!isLoading && !isEmpty && Array.isArray(data?.medias) && (
                <div className="flex flex-col gap-2 p-4 w-100 overflow-y-auto scroll_div">
                    {data.medias.map((c) => {
                        const male = Number(c?.gender?.MALE ?? 0);
                        const female = Number(c?.gender?.FEMALE ?? 0);
                        const total = male + female;
                        const malePct = total ? Math.round((male / total) * 100) : 0;
                        const femalePct = total ? 100 - malePct : 0;

                        return (
                            <div key={c.id} className="w-100 card analytics-card">
                                <div className="flex justify-between ">
                                    <div className="w-25 analytics-card__title">
                                        <i className="fa-solid fa-images  me-2" />
                                        <span className="text-truncate">{c.name}</span>
                                    </div>

                                    <div className="w-25 analytics-card__metric">
                                        <div className="small text-muted">{t('devices.setup_analytics_views')}</div>
                                        <div className="fw-semibold">
                                            <i className="fa-solid fa-chart-bar me-1" />
                                            {c.views ?? 0}
                                        </div>
                                    </div>

                                    <div className="w-25 analytics-card__metric">
                                        <div className="small text-muted">{t('devices.setup_analytics_gender')}</div>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="stacked-bar" aria-label="Gender distribution">
                                                <div
                                                    className="stacked-bar__male"
                                                    style={{ width: `${malePct}%` }}
                                                    title={`Male: ${malePct}% (${male})`}
                                                />
                                                <div
                                                    className="stacked-bar__female"
                                                    style={{ width: `${femalePct}%` }}
                                                    title={`Female: ${femalePct}% (${female})`}
                                                />
                                            </div>

                                            <div className="small text-nowrap text-muted">
                                                <i className="fa-solid fa-person me-1" /> {malePct}%&nbsp;
                                                <span className="mx-1">•</span>
                                                <i className="fa-solid fa-person-dress me-1" /> {femalePct}%
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-light analytics-card__btn"
                                        title={t('devices.setup_view_details')}
                                        onClick={() => {
                                            setDataItem(c)
                                            dispatch(toggleDialog("analyticsDetailDialog"))

                                        }}
                                    >
                                        <i className="fa-solid fa-eye" />
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            )}

            {openDialog.analyticsDetailDialog && dataItem && (
                <AnalyticsDetailDialog dataItem={dataItem} />
            )}

        </Dialog>
    );
}

export default AnalyticsDialog;
